import {Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import {VideoRecorderComponent} from "../video-recorder/video-recorder.component";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {SelectFilterV2Component} from "../../../../components/common/select-filter-v2/select-filter-v2.component";
import {SelectElementV2} from "../../../../components/common/select/selectElementV2";
import {FormFieldModule} from "../../../core/component/form-field/form-field.module";
import {DialogController} from "../../dialog/dialogController";
import {
  VideoRecorderDialogComponent, VideoRecorderDialogOutput
} from "../video-recorder-dialog/video-recorder-dialog.component";
import {ButtonModule} from "../../../core/component/button/button.module";
import {CardModule} from "../../../core/component/card/card.module";
import {FileUploadModule} from "../../../../components/common/file-upload/file-upload.module";
import {
  DrawingOnImageDialogComponent, DrawingOnImageDialogOutput
} from "../../drawing/drawing-on-image-dialog/drawing-on-image-dialog.component";
import {DrawingData} from "../../drawing/drawing-on-image/drawingOnImageDrawingElement.model";
import {
  FileUploadDialogComponent, FileUploadDialogInput, FileUploadDialogOutput
} from "../../../../components/common/file-upload/file-upload-dialog/file-upload-dialog.component";
import {ImageHelper} from "../../image/imageHelper";
import {Base64Utils} from "../../../core/utils/base64.utils";
import {
  VideoAnalysisImageDrawingDialogComponent, VideoAnalysisImageDrawingDialogOutput
} from "../video-analysis-image-drawing-dialog/video-analysis-image-drawing-dialog.component";
import {FileNameUtils} from "../../../core/utils/fileName.utils";

@Component({
  selector: 'app-video-analysis',
  standalone: true,
  imports: [CommonModule,
    VideoRecorderComponent,
    FileUploadDialogComponent,
    FormFieldModule, SelectFilterV2Component, ButtonModule, CardModule, FileUploadModule],
  templateUrl: './video-analysis.component.html',
})
export class VideoAnalysisComponent implements OnDestroy {
  @ViewChild('videoPlayer', { static: true }) videoPlayer!: ElementRef<HTMLVideoElement>;

  @Input() inputData: VideoAnalysisData;
  @Input() downloadedVideoPrefix: string;
  @Input() categoriesData: VideoAnalysisCategoriesData;
  @Output() changedEvent: EventEmitter<VideoAnalysisData> = new EventEmitter();

  maximized = false;
  formGroup: FormGroup;
  inputTypeElements: SelectElementV2<string>[] = [];
  playBackRates: number[] = [0.25, 0.5, 0.75, 1, 2, 5];


  inputVideoUrl: string;
  inputVideoBlob: Blob;
  pictures: Picture[] = [];

  constructor(private formBuilder: FormBuilder,
              private dialogController: DialogController) {
    this.formGroup = this.formBuilder.group({
      playbackRate: new FormControl(1),
    });
  }

  ngOnDestroy() {
    this.clear();
  }

  clear() {
    if (this.inputVideoUrl){
      URL.revokeObjectURL(this.inputVideoUrl);
    }
    this.inputVideoUrl = undefined;
    this.inputVideoBlob = undefined;
    this.pictures.forEach(value => {
      URL.revokeObjectURL(value.backgroundImageUrl);
      if (value.mergedImageUrl) {
        URL.revokeObjectURL(value.mergedImageUrl);
      }
    });
    this.pictures = [];
  }

  openVideoRecording() {
    this.dialogController.open(VideoRecorderDialogComponent, undefined, {
      title: 'Enregistrement vidéo',
      width: '95%'
    }).afterClosed()
        .toPromise()
        .then((output: VideoRecorderDialogOutput) => {
          if (output) {
            this.inputVideoBlob = output.blob;
            this.inputVideoUrl = URL.createObjectURL(output.blob);
          }
        })
  }

  uploadFile() {
    this.dialogController.open(FileUploadDialogComponent, {
      nbMaxFiles: 1,
      allowedFileExtensions: ['mov', 'mp4'],
      maxSizeInMo: 50
    } as FileUploadDialogInput, {
      title: 'Uploader une vidéo',
    }).afterClosed()
        .toPromise()
        .then((result: FileUploadDialogOutput) => {
          if (result?.files?.length > 0) {
            this.inputVideoBlob = result.files[0]._file;
            this.inputVideoUrl = URL.createObjectURL(result.files[0]._file);
          }
        })
  }

  onChangePlaybackRate(playRate: number) {
    console.log('onChangePlaybackRate', playRate, this.videoPlayer, this.videoPlayer.nativeElement);
    this.formGroup.get('playbackRate').setValue(playRate);
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.playbackRate = playRate;
    }
  }

  captureImage() {
    this.videoPlayer.nativeElement.pause();
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Dessiner le frame actuel de la vidéo sur le canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          ImageHelper.resizeImageBlobIfNeeded(blob, 1080, 1080).then(value => {
            const picture: Picture = new Picture();
            picture.time = video.currentTime;
            picture.drawingData = undefined;
            picture.backgroundImageUrl = URL.createObjectURL(blob);
            Base64Utils.blobToBase64(blob).then(value1 => {
              picture.backgroundImage = value1;
              this.pictures.push(picture);
              this.editPicture(picture);
            });

          })
        }else {
          console.log('Error during blob creation');
        }
      })
    }
  }

  editPicture(picture: Picture) {
    this.dialogController.open(VideoAnalysisImageDrawingDialogComponent, {
      backgroundUrl: picture.backgroundImageUrl,
      drawingData: !picture?.drawingData?.drawingData?.items ? undefined : {
        items: picture.drawingData.drawingData.items.map(value => {
          return {
            typeValue: value.typeValue,
            subTypeValue: value.subTypeValue,
            common: value.common,
            properties: value.properties
          }
        })
      },
      categoriesData: this.categoriesData,
      categoryName: picture.categoryName,
    }, {
      width: '95%',
      height: '100%',
      title: 'Analyse de l\'image'
    }).afterClosed()
        .toPromise()
        .then((result: VideoAnalysisImageDrawingDialogOutput) => {
          if (result?.drawingData) {
            picture.drawingData = {
              drawingData: result.drawingData,
              image: result.image,
            };
            picture.mergeImages();
            picture.categoryName = result.categoryName;
          }
          this.sendChangedEvent();
        });
  }

  removePicture(picture: Picture) {
    this.pictures.splice(this.pictures.indexOf(picture), 1);
    this.sendChangedEvent();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.videoPlayer) {
        if (this.videoPlayer.nativeElement.paused) {
          this.videoPlayer.nativeElement.play();
        } else {
          this.videoPlayer.nativeElement.pause();
        }
      }

    }
  }

  sendChangedEvent() {
    this.changedEvent.emit({
      pictures: this.pictures ? this.pictures : []
    });
  }

  download() {
    let extension = 'webm';
    let prefix = 'video';
    if (this.inputVideoBlob) {
      const blob: any = this.inputVideoBlob as any;
       if (this.inputVideoBlob.type === 'video/mp4') {
        extension = 'mp4';
      } else if (this.inputVideoBlob.type === 'video/quicktime') {
        extension = 'mov';
      }
    }

    if (this.downloadedVideoPrefix) {
      prefix = FileNameUtils.sanitizeFilenamePrefix(this.downloadedVideoPrefix);
    }

    const anchor = document.createElement('a');
    anchor.href = this.inputVideoUrl;
    anchor.download = prefix + '.' + extension;
    anchor.click();
    anchor.remove();
  }
}

class Picture {
  backgroundImageUrl: string;
  backgroundImage: string;
  time: number;
  drawingData: PictureDrawingData;
  categoryName: string;
  mergedImageUrl: string;

  mergeImages(): void {
    if (this.backgroundImage && this.drawingData?.image) {
     ImageHelper.combineImagesFromBase64(this.backgroundImage, this.drawingData?.image)
          .then((blob) => {
            this.mergedImageUrl = URL.createObjectURL(blob);
          })
    }
  }

}

class PictureDrawingData {
  image: string;
  drawingData: DrawingData;
}

export class VideoAnalysisData {
  pictures: Picture[] = [];
}

export class VideoAnalysisCategoriesData {
  categories: VideoAnalysisCategoriesDataCategory[] = [];
}

export class VideoAnalysisCategoriesDataCategory {
  name: string;
  label: string;
  options: VideoAnalysisCategoriesDataCategoryOptions[] = [];
}

export class VideoAnalysisCategoriesDataCategoryOptions {
  label: string;
  name: string;
  element: string;
  subType: string;
  color: string;
  thickness: number;
  size: number;
}
